import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
  formatDate, 
  formatNumber, 
  getStatusBadgeClass, 
  capitalize, 
  debounce,
  getPlanColor
} from '../utils/helpers';
import {
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  User,
  Mail,
  Calendar,
  Shield,
  CreditCard,
  Plus,
  Download,
  RefreshCw
} from 'lucide-react';

const Users = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    plan: '',
    isActive: '',
    dateRange: 'all'
  });
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [filters]);

  // Debounced search
  const debouncedSearch = debounce((searchTerm) => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
  }, 500);

  const fetchUsers = async (page = 1) => {
    try {
      setLoading(true);
      
      // Build params object, only including non-empty values
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      // Only add filters if they have values
      if (filters.search && filters.search.trim()) {
        params.append('search', filters.search.trim());
      }
      if (filters.role && filters.role.trim()) {
        params.append('role', filters.role);
      }
      if (filters.plan && filters.plan.trim()) {
        params.append('plan', filters.plan);
      }
      if (filters.isActive && filters.isActive.trim()) {
        params.append('isActive', filters.isActive);
      }
      if (filters.dateRange && filters.dateRange !== 'all') {
        if (filters.dateRange === 'custom') {
          if (customDateRange.startDate) {
            params.append('startDate', customDateRange.startDate);
          }
          if (customDateRange.endDate) {
            params.append('endDate', customDateRange.endDate);
          }
        } else {
          params.append('dateRange', filters.dateRange);
        }
      }

      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Fetch users error:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = async (userData) => {
    try {
      setActionLoading(true);
      await api.put(`/admin/users/${selectedUser._id}`, userData);
      toast.success('User updated successfully');
      setShowEditModal(false);
      fetchUsers(pagination.currentPage);
    } catch (error) {
      console.error('Update user error:', error);
      toast.error(error.response?.data?.message || 'Failed to update user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setActionLoading(true);
      await api.delete(`/admin/users/${selectedUser._id}`);
      toast.success('User deleted successfully');
      setShowDeleteModal(false);
      fetchUsers(pagination.currentPage);
    } catch (error) {
      console.error('Delete user error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setActionLoading(false);
    }
  };

  const exportUsers = async () => {
    try {
      const response = await api.get('/admin/users/export', {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `users-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Users exported successfully');
    } catch (error) {
      console.error('Export users error:', error);
      toast.error('Failed to export users');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="mt-1 text-sm text-gray-600">
            Manage user accounts and permissions
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={exportUsers}
            className="btn-secondary"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
          <button
            onClick={() => fetchUsers(pagination.currentPage)}
            className="btn-secondary"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.totalUsers || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Shield className="w-5 h-5 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Active Users</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.activeUsers || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Pro Users</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.proPlan || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Shield className="w-5 h-5 text-orange-600" />
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-600">Admins</p>
                <p className="text-xl font-bold text-gray-900">
                  {formatNumber(stats.adminUsers || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search users..."
                className="input pl-10"
                onChange={(e) => debouncedSearch(e.target.value)}
              />
            </div>
            
            <select
              className="input"
              value={filters.dateRange}
              onChange={(e) => {
                setFilters(prev => ({ ...prev, dateRange: e.target.value }));
                if (e.target.value !== 'custom') {
                  setCustomDateRange({ startDate: '', endDate: '' });
                }
              }}
            >
              <option value="all">All Time</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
              <option value="custom">Custom Range</option>
            </select>
            
            {filters.dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  className="input"
                  value={customDateRange.startDate}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  placeholder="Start Date"
                  max={customDateRange.endDate || undefined}
                />
                <input
                  type="date"
                  className="input"
                  value={customDateRange.endDate}
                  onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  placeholder="End Date"
                  min={customDateRange.startDate || undefined}
                />
              </>
            )}
            
            <select
              className="input"
              value={filters.role}
              onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
            >
              <option value="">All Roles</option>
              <option value="user">User</option>
              <option value="admin">Admin</option>
            </select>
            
            <select
              className="input"
              value={filters.plan}
              onChange={(e) => setFilters(prev => ({ ...prev, plan: e.target.value }))}
            >
              <option value="">All Plans</option>
              <option value="guest">Guest</option>
              <option value="free">Free</option>
              <option value="basic">Basic</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
            </select>
            
            <select
              className="input"
              value={filters.isActive}
              onChange={(e) => setFilters(prev => ({ ...prev, isActive: e.target.value }))}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="loading-spinner w-8 h-8"></div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Plan</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Usage</th>
                    <th>Joined</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr 
                      key={user._id} 
                      onClick={() => navigate(`/users/${user._id}`)}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <td>
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {user.name}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center">
                              <Mail className="w-3 h-3 mr-1" />
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`text-sm font-medium ${getPlanColor(user.plan)}`}>
                          {capitalize(user.plan)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(user.role)}`}>
                          {capitalize(user.role)}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${getStatusBadgeClass(user.isActive ? 'active' : 'inactive')}`}>
                          {user.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div className="text-sm text-gray-900">
                          {formatNumber(user.usageCount || 0)}
                        </div>
                        <div className="text-xs text-gray-500">
                          of {user.usageLimit == '-1' ? 'Unlimited' : formatNumber(user.usageLimit || 0)}
                        </div>
                      </td>
                      <td>
                        <div className="text-sm text-gray-900">
                          {formatDate(user.createdAt)}
                        </div>
                      </td>
                      <td>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUser(user);
                              setShowEditModal(true);
                            }}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                         {/* <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowDeleteModal(true);
                            }}
                            className="text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button> */}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Showing {((pagination.currentPage - 1) * 20) + 1} to {Math.min(pagination.currentPage * 20, pagination.totalUsers)} of {pagination.totalUsers} users
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => fetchUsers(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => fetchUsers(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          onSave={handleEditUser}
          onClose={() => setShowEditModal(false)}
          loading={actionLoading}
        />
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedUser && (
        <DeleteUserModal
          user={selectedUser}
          onConfirm={handleDeleteUser}
          onClose={() => setShowDeleteModal(false)}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ user, onSave, onClose, loading }) => {
  const [formData, setFormData] = useState({
    name: user.name || '',
    email: user.email || '',
    role: user.role || 'user',
    plan: user.plan || 'free',
    isActive: user.isActive !== false,
    usageLimit: user.usageLimit || 5,
    usageCount: user.usageCount || 0,
    subscription: {
      status: user.subscription?.status || 'active',
      provider: user.subscription?.provider || 'lemonsqueezy',
      startDate: user.subscription?.startDate ? new Date(user.subscription.startDate).toISOString().split('T')[0] : '',
      endDate: user.subscription?.endDate ? new Date(user.subscription.endDate).toISOString().split('T')[0] : '',
      currentPeriodStart: user.subscription?.currentPeriodStart ? new Date(user.subscription.currentPeriodStart).toISOString().split('T')[0] : '',
      currentPeriodEnd: user.subscription?.currentPeriodEnd ? new Date(user.subscription.currentPeriodEnd).toISOString().split('T')[0] : '',
      gracePeriodEnd: user.subscription?.gracePeriodEnd ? new Date(user.subscription.gracePeriodEnd).toISOString().split('T')[0] : '',
      lemonSqueezySubscriptionId: user.subscription?.lemonSqueezySubscriptionId || '',
      billingCycle: user.subscription?.billingCycle || 'monthly'
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Edit User</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                className="input"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                className="input"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>

            {/* <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                className="input"
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div> */}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Plan
              </label>
              <select
                className="input"
                value={formData.plan}
                onChange={(e) => setFormData(prev => ({ ...prev, plan: e.target.value }))}
              >
                <option value="guest">Guest</option>
                <option value="free">Free</option>
                <option value="basic">Basic</option>
                <option value="pro">Pro</option>
                <option value="premium">Premium</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Usage Limit
                <span className="text-xs text-gray-500 ml-2">(Enter -1 for unlimited)</span>
              </label>
              <input
                type="number"
                className="input"
                value={formData.usageLimit}
                onChange={(e) => setFormData(prev => ({ ...prev, usageLimit: parseInt(e.target.value) || 0 }))}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Usage Count
              </label>
              <div className="flex items-center space-x-2">
                <input
                  type="number"
                  className="input flex-1"
                  value={formData.usageCount}
                  onChange={(e) => setFormData(prev => ({ ...prev, usageCount: parseInt(e.target.value) || 0 }))}
                />
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, usageCount: 0 }))}
                  className="btn-secondary whitespace-nowrap"
                  title="Reset usage count to 0"
                >
                  Reset
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Current: {formData.usageCount} / {formData.usageLimit === -1 ? 'Unlimited' : formData.usageLimit}
              </p>
            </div>

            {/* Subscription Details Section */}
            {(
              <div className="border-t pt-4 mt-4">
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Subscription Details</h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      className="input"
                      value={formData.subscription.status}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        subscription: { ...prev.subscription, status: e.target.value }
                      }))}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="cancelled">Cancelled</option>
                      <option value="expired">Expired</option>
                      <option value="past_due">Past Due</option>
                      <option value="grace_period">Grace Period</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Provider
                    </label>
                    <select
                      className="input"
                      value={formData.subscription.provider}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        subscription: { ...prev.subscription, provider: e.target.value }
                      }))}
                    >
                      <option value="lemonsqueezy">LemonSqueezy</option>
                      <option value="manual">Manual</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Cycle
                    </label>
                    <select
                      className="input"
                      value={formData.subscription.billingCycle}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        subscription: { ...prev.subscription, billingCycle: e.target.value }
                      }))}
                    >
                      <option value="monthly">Monthly</option>
                      <option value="yearly">Yearly</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LemonSqueezy Subscription ID
                    </label>
                    <input
                      type="text"
                      className="input"
                      value={formData.subscription.lemonSqueezySubscriptionId}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        subscription: { ...prev.subscription, lemonSqueezySubscriptionId: e.target.value }
                      }))}
                      placeholder="Enter subscription ID"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        className="input"
                        value={formData.subscription.startDate}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          subscription: { ...prev.subscription, startDate: e.target.value }
                        }))}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        className="input"
                        value={formData.subscription.endDate}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          subscription: { ...prev.subscription, endDate: e.target.value }
                        }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Period Start
                      </label>
                      <input
                        type="date"
                        className="input"
                        value={formData.subscription.currentPeriodStart}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          subscription: { ...prev.subscription, currentPeriodStart: e.target.value }
                        }))}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Period End
                      </label>
                      <input
                        type="date"
                        className="input"
                        value={formData.subscription.currentPeriodEnd}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          subscription: { ...prev.subscription, currentPeriodEnd: e.target.value }
                        }))}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grace Period End
                    </label>
                    <input
                      type="date"
                      className="input"
                      value={formData.subscription.gracePeriodEnd}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        subscription: { ...prev.subscription, gracePeriodEnd: e.target.value }
                      }))}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isActive"
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                checked={formData.isActive}
                onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
              />
              <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
                Active User
              </label>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn-secondary"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="loading-spinner w-4 h-4 mr-2"></div>
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// Delete User Modal Component
const DeleteUserModal = ({ user, onConfirm, onClose, loading }) => {
  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Delete User</h3>
          <p className="text-sm text-gray-600 mb-6">
            Are you sure you want to delete <strong>{user.name}</strong>? This action cannot be undone.
          </p>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              className="btn-danger"
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="loading-spinner w-4 h-4 mr-2"></div>
                  Deleting...
                </>
              ) : (
                'Delete User'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Users;